import type { CSSProperties, ReactNode } from 'react';
import { Icon, type IconName } from './icons';
import { ToastHost as SharedToastHost } from '@orgazone/ui-web';
import { useApp } from '../state';
import type { DataState } from '../useResource';

// Primitives partagées de la console. Système de style = variables CSS (thème/densité) + styles en
// ligne (patron apps/store/web), jamais de framework CSS. Couleurs sémantiques DISTINCTES du teal
// de marque (le teal = accent d'interface ; le vert « ok » = état de donnée sain).

export type BadgeKind = 'recu' | 'traduit' | 'pousse' | 'ok' | 'warn' | 'err' | 'off';
const BADGE: Record<BadgeKind, [string, string]> = {
  recu: ['var(--ink-100)', 'var(--ink-700)'],
  traduit: ['var(--violet-bg)', 'var(--violet-fg)'],
  pousse: ['var(--blue-bg)', 'var(--blue-fg)'],
  ok: ['var(--ok-bg)', 'var(--ok-fg)'],
  warn: ['var(--amber-bg)', 'var(--amber-fg)'],
  err: ['var(--rose-bg)', 'var(--rose-fg)'],
  off: ['var(--ink-100)', 'var(--ink-500)'],
};

/** Pastille de statut (progression reçu→traduit→poussé→confirmé, ou attention/erreur/repos). */
export function Badge({ kind, children }: { kind: BadgeKind; children: ReactNode }) {
  const [bg, fg] = BADGE[kind];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 20, padding: '0 8px', borderRadius: 'var(--r-pill)', fontSize: 'var(--t-meta)', fontWeight: 600, whiteSpace: 'nowrap', background: bg, color: fg }}>
      {children}
    </span>
  );
}

export type DotKind = 'ok' | 'warn' | 'err' | 'off';
const DOT: Record<DotKind, string> = { ok: 'var(--ok)', warn: 'var(--amber)', err: 'var(--rose)', off: 'var(--ink-400)' };

/** Point d'état (santé transport, verdict de ligne). Halo doux via color-mix. */
export function Dot({ kind }: { kind: DotKind }) {
  const col = DOT[kind];
  return <span style={{ width: 8, height: 8, borderRadius: 'var(--r-pill)', flexShrink: 0, background: col, boxShadow: `0 0 0 3px color-mix(in oklch, ${col} 22%, transparent)` }} />;
}

/** Surface carte (papier + bordure + ombre légère). */
export function Card({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', boxShadow: 'var(--sh-sm)', ...style }}>{children}</div>;
}

/** Bouton d'action — respecte le rôle : grisé + non cliquable en lecture seule (le vrai refus passe
 *  aussi par `guard()` côté handler et DEVRA être appliqué côté serveur). */
export function Btn({ children, onClick, icon, size = 'md', variant = 'primary', disabled, mutating = true, style }: {
  children: ReactNode; onClick?: () => void; icon?: IconName; size?: 'sm' | 'md';
  variant?: 'primary' | 'ghost' | 'danger'; disabled?: boolean; mutating?: boolean; style?: CSSProperties;
}) {
  const { admin } = useApp();
  const off = disabled || (mutating && !admin);
  // ⚠️ AUCUNE métrique locale : l'EDI est une console interne, elle suit la charte SANS exception
  // (décision utilisateur 2026-08-13 — « je veux la même chose partout »). Ses anciennes hauteurs
  // 28/30/34 et son rayon 9 disparaissent au profit de la recette et de son format dense.
  const danger = variant === 'danger'
    ? { border: `1px solid ${off ? 'var(--ink-200)' : 'var(--rose)'}`, background: off ? 'var(--ink-100)' : 'var(--rose-bg)', color: off ? 'var(--ink-400)' : 'var(--rose-fg)', cursor: off ? 'not-allowed' : 'pointer', borderRadius: 'var(--r-sm)', height: size === 'sm' ? 28 : 36, padding: size === 'sm' ? '0 11px' : '0 16px', fontSize: size === 'sm' ? 'var(--t-dense)' : 'var(--t-body)', fontWeight: 700 }
    : null;
  const recette = variant === 'ghost' ? 'btn-ghost' : 'btn-cta';
  return (
    <button type="button" disabled={off} onClick={onClick}
      className={variant === 'danger' ? undefined : size === 'sm' ? `${recette} sm` : recette}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 7, ...danger, ...style }}>
      {icon && <Icon name={icon} size={14} />}
      {children}
    </button>
  );
}

/** Spinner de progression (anime `spin`). */
export function Spinner({ size = 16 }: { size?: number }) {
  return <span style={{ display: 'inline-block', width: size, height: size, borderRadius: '50%', border: '2px solid var(--ink-200)', borderTopColor: 'var(--teal-500)', animation: 'spin .7s linear infinite' }} />;
}

/** Toast ancré en bas au centre (ton coloré). Piloté par l'état applicatif. */
/**
 * Hôte des toasts — DÉLÈGUE au rendu partagé `@orgazone/ui-web`, qui lit la spécification de design
 * commune (`@orgazone/ui-tokens`). Ne reste ici que ce qui appartient à l'EDI : son registre d'icônes et
 * les couleurs de son thème. ⚠️ Le rendu n'est plus réécrit : il était devenu un rectangle avec un point
 * de 8 px, et n'affichait qu'UN SEUL toast — une 2ᵉ action effaçait le message de la 1ʳᵉ (§23.0).
 */
export function ToastHost() {
  // Défauts CANONIQUES du rendu partagé (mêmes valeurs que celles que l'EDI passait à la main —
  // devenues le défaut de la plateforme le 2026-08-05, plus rien à fournir).
  return <SharedToastHost />;
}

/** En-tête d'écran (titre + sous-titre + actions à droite). */
export function PageHead({ title, sub, actions }: { title: string; sub?: string; actions?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 18 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h1 className="page-title">{title}</h1>
        {sub && <div style={{ marginTop: 3, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{sub}</div>}
      </div>
      {actions}
    </div>
  );
}

/** Passerelle des états de données §7 : chargement / échec (trace + Réessayer) / vide / normal.
 *  « L'écran ne ment jamais » : rien n'est deviné ni mis en cache sur un échec. `onRetry` = vrai refetch
 *  (les écrans câblés le fournissent) ; à défaut on réinitialise juste l'état (dernier recours). */
/**
 * État vide D'UN BLOC (et non de l'écran entier) : garde au bloc sa forme et son titre, en disant
 * simplement qu'il n'a rien à montrer.
 * ⚠️ Un tableau de bord qui disparaît derrière un seul message central prive l'opérateur de sa carte
 * mentale : il ne sait plus ce que l'écran surveille, ni où regarderait l'information si elle existait.
 * Zéro n'est pas « rien » — c'est une mesure, et elle a sa place dans le bloc qui la porte.
 */
export function BlockEmpty({ icon = 'layers', text, hint }: { icon?: IconName; text: string; hint?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '18px 16px', color: 'var(--ink-500)' }}>
      <span style={{ width: 30, height: 30, borderRadius: 'var(--r-sm)', background: 'var(--ink-100)', color: 'var(--ink-400)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        <Icon name={icon} size={15} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)' }}>{text}</div>
        {hint && <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-400)', marginTop: 2 }}>{hint}</div>}
      </div>
    </div>
  );
}

/**
 * Garde d'affichage selon l'état de chargement.
 * ⚠️ `empty` est OPTIONNEL : l'omettre signifie « cet écran gère lui-même son état vide » — la garde
 * laisse alors passer les enfants. Utile quand un zéro doit rester LISIBLE bloc par bloc (supervision)
 * plutôt qu'effacer toute la page. `loading` et `failed` restent pris en charge ici : eux ne peuvent
 * rien rendre d'utile, et un écran à moitié peint pendant une panne serait pire que pas d'écran.
 */
/**
 * Rend l'écran, ou l'état qui le remplace (chargement / échec / vide).
 *
 * ⚠️ L'état est REÇU, jamais lu dans un contexte partagé : il était global à toute la console, et la
 * réponse tardive d'un écran quitté venait donc afficher son échec sur l'écran suivant (cf. `useResource`).
 * `state` et `onRetry` viennent tous deux du `useResource` de l'écran — ils vont ensemble.
 */
export function DataGate({ state, empty, onRetry, error, head, children }: {
  state: DataState; empty?: { title: string; text: string }; onRetry: () => void;
  /** Le message réel de l'échec (`useResource().error`) — affiché tel quel, jamais remplacé par une phrase rassurante. */
  error?: string | null;
  /**
   * L'en-tête de l'écran (titre, sous-titre, boutons) : rendu au-dessus du contenu ET de l'état vide.
   * ⚠️ JAMAIS DANS LES ENFANTS : l'état vide remplace les enfants, et le bouton qui crée le premier élément
   *    disparaissait exactement là où il est le seul geste utile (« Nouveau fournisseur »). Le chargement et
   *    l'échec, eux, remplacent tout — la maquette fait de même. Gardé par `__tests__/etat-par-ecran.test.ts`.
   */
  head?: ReactNode;
  children: ReactNode;
}) {
  if (state === 'loading') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, gap: 14, color: 'var(--ink-500)' }}>
        <Spinner size={26} /><span style={{ fontSize: 'var(--t-lead)' }}>Chargement…</span>
      </div>
    );
  }
  if (state === 'failed') {
    // La carte de la maquette. ⚠️ Sa phrase « l'API n'a pas répondu » n'est pas reprise : un refus (403, 500)
    // EST une réponse, et c'est son motif — affiché tel quel dans le bloc technique — qui dit ce qui s'est passé.
    return (
      <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
        <Card style={{ maxWidth: 560, width: '100%', padding: 26, display: 'flex', gap: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', display: 'grid', placeItems: 'center', flexShrink: 0 }}><Icon name="alert" size={20} /></div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)' }}>Impossible de charger cet écran</div>
            <p style={{ margin: '7px 0 12px', fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.55 }}>
              {error ? 'Aucune donnée n’est affichée — rien n’est deviné ni mis en cache.' : 'Le service n’a rendu aucun motif. Aucune donnée n’est affichée — rien n’est deviné ni mis en cache.'}
            </p>
            {error && (
              <pre style={{ margin: '0 0 14px', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', color: 'var(--rose-fg)', background: 'var(--rose-bg)', padding: '10px 12px', borderRadius: 'var(--r-ctrl)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.5 }}>{error}</pre>
            )}
            <Btn icon="refresh" mutating={false} onClick={onRetry}>Réessayer</Btn>
          </div>
        </Card>
      </div>
    );
  }
  if (state === 'empty' && empty) {
    return (
      <>
      {head}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 70, gap: 10, textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: 'var(--r-sm)', background: 'var(--ink-100)', color: 'var(--ink-400)', display: 'grid', placeItems: 'center' }}><Icon name="layers" size={24} /></div>
        <div style={{ fontSize: 'var(--t-h4)', fontWeight: 600, color: 'var(--ink-700)' }}>{empty.title}</div>
        <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', maxWidth: 440 }}>{empty.text}</div>
      </div>
      </>
    );
  }
  return <>{head}{children}</>;
}
